import React, { useState, useEffect, useMemo } from 'react';
import { apiCall } from '../../../../services/realApi';
import { Modal, Input, Button } from '../../../../components/ui';
import { MODULES, PERMISSIONS, moduleDescriptions } from '../../../../utils/permissions';
import { usePermissions } from '../../../../hooks/usePermissions';
import { FiAlertCircle as AlertCircle, FiInfo as Info, FiChevronDown as ChevronDown, FiChevronUp as ChevronUp } from 'react-icons/fi';
import { useAuth } from '../../../../contexts/AuthContext';

interface AddUserModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  defaultRole?: string | null;
}

const AddUserModal: React.FC<AddUserModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  defaultRole = null // 'client' or 'partner' - when set, pre-selects and locks the role
}) => {
  const { user: currentUser } = useAuth();
  const { hasPermission, isAdmin } = usePermissions();
  const [formData, setFormData] = useState<{
    name: string;
    email: string;
    role: string;
    client_id: string;
    partner_id: string;
    client_role: string;
    modules: string[];
    permissions: string[];
  }>({
    name: '',
    email: '',
    role: 'client',
    client_id: '',
    partner_id: '',
    client_role: 'regular_user', // Default client role
    modules: [],
    permissions: []
  });
  const [clients, setClients] = useState<{ id: string; name: string; [key: string]: unknown }[]>([]);
  const [partners, setPartners] = useState<{ id: string; name: string; [key: string]: unknown }[]>([]);
  const [loadingClients, setLoadingClients] = useState(false);
  const [loadingPartners, setLoadingPartners] = useState(false);
  const [errors, setErrors] = useState<Record<string, string | undefined>>({});
  const [loading, setLoading] = useState(false);
  const [showModulesSection, setShowModulesSection] = useState(false);
  const [showPermissionsSection, setShowPermissionsSection] = useState(false);
  const [expandedPermissionModules, setExpandedPermissionModules] = useState<Record<string, boolean>>({});
  const [allPermissions, setAllPermissions] = useState<{ id?: string; name: string; description?: string }[]>([]);
  const [permissionsLoading, setPermissionsLoading] = useState(false);

  const canViewClients = hasPermission(PERMISSIONS.CLIENTS_VIEW) || hasPermission(PERMISSIONS.CLIENTS_MANAGE);
  const canManagePartners = hasPermission(PERMISSIONS.PARTNERS_MANAGE);
  const canViewPartners = hasPermission(PERMISSIONS.PARTNERS_VIEW) || canManagePartners;
  const isSystemAdmin = isAdmin || hasPermission(PERMISSIONS.SYSTEM_ADMIN);
  const hasClientScope = Boolean(currentUser?.client_id);
  const hasPartnerScope = Boolean(currentUser?.partner_id);

  const allModules = useMemo(() => Object.values(MODULES), []);
  const availableModules = useMemo(() => {
    if (isSystemAdmin) return allModules;
    return currentUser?.aws_accessible_modules || [];
  }, [currentUser, isSystemAdmin, allModules]);
  const availablePermissions = useMemo(
    () => {
      if (isSystemAdmin && allPermissions.length > 0) return allPermissions;
      return currentUser?.permissions || [];
    },
    [currentUser, isSystemAdmin, allPermissions]
  );

  const moduleLabels = {
    [MODULES.HUB]: 'Hub',
    [MODULES.PLATFORMOPS]: 'CloudOps',
    [MODULES.DATAOPS]: 'DataOps',
    [MODULES.GENAIOPS]: 'GenAIOps',
    [MODULES.AGENTOPS]: 'AgentOps'
  };

  const permissionModuleOrder = [
    MODULES.HUB,
    MODULES.PLATFORMOPS,
    MODULES.DATAOPS,
    MODULES.GENAIOPS,
    MODULES.AGENTOPS
  ];

  const getPermissionModule = (permission: string | { name?: string }) => {
    const permName = typeof permission === 'string' ? permission : (permission?.name || '');
    const resource = permName.split('.')[0];

    switch (resource) {
      case 'platformops':
        return MODULES.PLATFORMOPS;
      case 'dataops':
        return MODULES.DATAOPS;
      case 'genaiops':
        return MODULES.GENAIOPS;
      case 'agentops':
        return MODULES.AGENTOPS;
      default:
        return MODULES.HUB;
    }
  };

  const groupedPermissions = useMemo(() => {
    return availablePermissions.reduce<Record<string, Array<{ id?: string; name: string; description?: string }>>>((acc, permission) => {
      const moduleKey = getPermissionModule(permission);
      if (!acc[moduleKey]) {
        acc[moduleKey] = [];
      }
      acc[moduleKey].push(permission);
      return acc;
    }, {});
  }, [availablePermissions]);

  const loadAllPermissions = async () => {
    if (!isSystemAdmin) return;
    setPermissionsLoading(true);
    try {
      const data = await apiCall('/roles/permissions');
      setAllPermissions(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error loading permissions:', err);
      setAllPermissions([]);
    } finally {
      setPermissionsLoading(false);
    }
  };

  const handleChange = (field: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const value = e.target.value;
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: undefined
      }));
    }
  };

  const handleModuleToggle = (module: string) => {
    setFormData(prev => {
      const currentModules = prev.modules || [];
      const isSelected = currentModules.includes(module);
      return {
        ...prev,
        modules: isSelected
          ? currentModules.filter(m => m !== module)
          : [...currentModules, module]
      };
    });
  };

  const handlePermissionToggle = (permission: string) => {
    setFormData(prev => {
      const currentPermissions = prev.permissions || [];
      const isSelected = currentPermissions.includes(permission);
      return {
        ...prev,
        permissions: isSelected
          ? currentPermissions.filter(p => p !== permission)
          : [...currentPermissions, permission]
      };
    });
  };

  const handleSelectAllModules = () => {
    setFormData(prev => ({
      ...prev,
      modules: [...availableModules]
    }));
  };

  const handleDeselectAllModules = () => {
    setFormData(prev => ({
      ...prev,
      modules: []
    }));
  };

  const handleSelectAllPermissions = () => {
    const allPermissionNames = availablePermissions.map(p => p.name);
    setFormData(prev => ({
      ...prev,
      permissions: allPermissionNames
    }));
  };

  const handleDeselectAllPermissions = () => {
    setFormData(prev => ({
      ...prev,
      permissions: []
    }));
  };

  const togglePermissionModule = (moduleKey: string) => {
    setExpandedPermissionModules(prev => ({
      ...prev,
      [moduleKey]: !prev[moduleKey]
    }));
  };

  const handleModulePermissionsToggle = (moduleKey: string) => {
    const modulePermissions = groupedPermissions[moduleKey] || [];
    const modulePermissionNames = modulePermissions.map(p => p.name);
    const allSelected = modulePermissionNames.every(name => formData.permissions?.includes(name));

    setFormData(prev => {
      const currentPermissions = prev.permissions || [];
      if (allSelected) {
        // Deselect all in this module
        return {
          ...prev,
          permissions: currentPermissions.filter(p => !modulePermissionNames.includes(p))
        };
      } else {
        // Select all in this module
        const newPermissions = Array.from(new Set([...currentPermissions, ...modulePermissionNames]));
        return {
          ...prev,
          permissions: newPermissions
        };
      }
    });
  };

  // Load clients and partners when modal opens
  useEffect(() => {
    if (isOpen) {
      // Set role based on defaultRole prop if provided, otherwise use default logic
      if (defaultRole) {
        setFormData(prev => ({ ...prev, role: defaultRole }));
      } else if (canManagePartners || canViewClients) {
        // Default to client for backward compatibility
        setFormData(prev => ({ ...prev, role: 'client' }));
      }

      // Load clients for partners and clients (only if creating client users or role not locked)
      if (canViewClients && (!defaultRole || defaultRole === 'client')) {
        loadClients();
      }

      // Load partners for aivilo_staff and partners (only if creating partner users or role not locked)
      if (canViewPartners && (!defaultRole || defaultRole === 'partner')) {
        loadPartners();
      }

      if (isSystemAdmin) {
        loadAllPermissions();
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, currentUser, defaultRole, canViewClients, canViewPartners, canManagePartners, isSystemAdmin]);

  // Initialize module and permission inheritance defaults
  useEffect(() => {
    if (isOpen && currentUser) {
      const userModules = availableModules || [];
      const userPermissions = availablePermissions || [];
      const defaultPermissions = userPermissions.map(p => p.name);

      setFormData(prev => ({
        ...prev,
        modules: [...userModules],
        permissions: defaultPermissions
      }));
    }
  }, [isOpen, currentUser, availableModules, availablePermissions]);

  const loadClients = async () => {
    setLoadingClients(true);
    try {
      const data = await apiCall('/clients');
      setClients(data || []);
      
      // If current user is a client, auto-select their client
      if (hasClientScope && data && currentUser) {
        const userClient = (data as { id: string }[]).find((c) => c.id === currentUser.client_id);
        if (userClient) {
          setFormData(prev => ({ ...prev, client_id: userClient.id }));
        }
      }
    } catch (err) {
      console.error('Error loading clients:', err);
      setClients([]);
    } finally {
      setLoadingClients(false);
    }
  };

  const loadPartners = async () => {
    setLoadingPartners(true);
    try {
      const data = await apiCall('/partners');
      setPartners(data || []);
      
      // If current user is a partner, auto-select their partner
      if (hasPartnerScope && data && currentUser) {
        const userPartner = (data as { id: string }[]).find((p) => p.id === currentUser.partner_id);
        if (userPartner) {
          setFormData(prev => ({ ...prev, partner_id: userPartner.id }));
        }
      }
    } catch (err) {
      console.error('Error loading partners:', err);
      setPartners([]);
    } finally {
      setLoadingPartners(false);
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (formData.email.trim()) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
        newErrors.email = 'Invalid email format';
      }
    } else {
      newErrors.email = 'Email is required';
    }

    if (!formData.role) {
      newErrors.role = 'Role is required';
    }

    // Validate client_id for client users (skip when user already has client scope - backend uses their client_id)
    if (formData.role === 'client' && !hasClientScope && !formData.client_id) {
      newErrors.client_id = 'Client is required for client users';
    }

    // Validate partner_id for partner users
    // aivilo_staff and partners (with permission) can create partners
    if (formData.role === 'partner' && canManagePartners && !formData.partner_id) {
      newErrors.partner_id = 'Partner is required for partner users';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setErrors({});

    try {
      const payload: Record<string, unknown> = {
        name: formData.name.trim(),
        email: formData.email.trim(),
        role: formData.role
      };

      // Add client_id or partner_id based on role
      if (formData.role === 'client') {
        // Only include client_id if user doesn't have client scope (backend auto-resolves for client users)
        if (formData.client_id) {
          payload.client_id = formData.client_id;
        }
        // Add client_role for client users
        if (formData.client_role) {
          payload.client_role = formData.client_role;
        }
      } else if (formData.role === 'partner') {
        if (formData.partner_id) {
          payload.partner_id = formData.partner_id;
        }
      }

      if ((formData.modules?.length ?? 0) > 0) {
        payload.modules = formData.modules;
      }

      if ((formData.permissions?.length ?? 0) > 0) {
        payload.permissions = formData.permissions;
      }

      await apiCall('/users', {
        method: 'POST',
        body: JSON.stringify(payload)
      });

      // Reset form
      setFormData({
        name: '',
        email: '',
        role: defaultRole ?? 'client',
        client_id: '',
        partner_id: '',
        client_role: 'regular_user',
        modules: [],
        permissions: []
      });
      setErrors({});
      setShowModulesSection(false);
      setShowPermissionsSection(false);
      setExpandedPermissionModules({});

      if (onSuccess) {
        onSuccess();
      }
      onClose();
    } catch (err: unknown) {
      console.error('Error adding user:', err);
      setErrors({
        submit: (err instanceof Error ? err.message : null) || 'Failed to add user. Please try again.'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      name: '',
      email: '',
      role: defaultRole ?? 'client',
      client_id: '',
      partner_id: '',
      client_role: 'regular_user',
      modules: [],
      permissions: []
    });
    setErrors({});
    setShowModulesSection(false);
    setShowPermissionsSection(false);
    setExpandedPermissionModules({});
    onClose();
  };

  const isPartner = hasPartnerScope;
  // Can only select role if not locked by defaultRole prop
  const canSelectRole = !defaultRole && canManagePartners;
  
  // Determine modal title based on defaultRole
  const modalTitle = defaultRole === 'partner'
    ? 'Add Partner User'
    : 'Add User';

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={modalTitle}
      maxWidth="max-w-2xl"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Info Banner */}
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 flex items-start">
          <Info className="w-5 h-5 text-blue-600 dark:text-blue-400 mr-3 mt-0.5 flex-shrink-0" />
          <div className="text-sm text-blue-800 dark:text-blue-300">
            <p className="font-medium mb-1">Permission Inheritance</p>
            <p>
              Select which modules and permissions you want the new user to inherit. 
              By default, all your available modules and permissions are selected.
            </p>
          </div>
        </div>

        {/* Name */}
        <Input
          label="Name"
          value={formData.name}
          onChange={handleChange('name')}
          placeholder="Enter user name"
          required
          error={errors.name}
        />

        {/* Email */}
        <Input
          label="Email"
          type="email"
          value={formData.email}
          onChange={handleChange('email')}
          placeholder="user@example.com"
          required
          error={errors.email}
        />

        {/* Role - Show for aivilo_staff and partners */}
        {canSelectRole && (
          <div>
            <label htmlFor="role" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Role <span className="text-red-500">*</span>
            </label>
            <select id="role"
              value={formData.role}
              onChange={handleChange('role')}
              required
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="client">Client</option>
              {canManagePartners && <option value="partner">Partner</option>}
              {isPartner && <option value="partner">Partner</option>}
            </select>
            {errors.role && (
              <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.role}</p>
            )}
          </div>
        )}

        {/* Client Selector - Show for client users (hidden when user already has client scope) */}
        {formData.role === 'client' && !hasClientScope && (
          <div>
            <label htmlFor="client" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Client <span className="text-red-500">*</span>
            </label>
            <select id="client"
              value={formData.client_id}
              onChange={handleChange('client_id')}
              required
              disabled={loadingClients}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <option value="">Select a client...</option>
              {clients.map((client) => (
                <option key={client.id} value={client.id}>
                  {client.name}
                </option>
              ))}
            </select>
            {errors.client_id && (
              <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.client_id}</p>
            )}
          </div>
        )}

        {/* Client Role Selector - Show for client users */}
        {formData.role === 'client' && (
          <div>
            <label htmlFor="client-role" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Client Role <span className="text-red-500">*</span>
            </label>
            <select id="client-role"
              value={formData.client_role}
              onChange={handleChange('client_role')}
              required
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="regular_user">Regular User</option>
              <option value="admin_user">Admin User</option>
              <option value="account_owner">Account Owner</option>
            </select>
            <div className="mt-2 space-y-1 text-xs text-gray-500 dark:text-gray-400">
              <p><span className="font-semibold">Account Owner:</span> Full control including ownership transfer and billing</p>
              <p><span className="font-semibold">Admin User:</span> Can manage users and organization settings</p>
              <p><span className="font-semibold">Regular User:</span> Standard access to assessments and features</p>
            </div>
          </div>
        )}

        {/* Partner Selector - Show for partner users (aivilo_staff and partners) */}
        {formData.role === 'partner' && (canManagePartners || isPartner) && (
          <div>
            <label htmlFor="partner" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Partner <span className="text-red-500">*</span>
            </label>
            <select id="partner"
              value={formData.partner_id}
              onChange={handleChange('partner_id')}
              required
              disabled={loadingPartners || isPartner}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <option value="">Select a partner...</option>
              {partners.map((partner) => (
                <option key={partner.id} value={partner.id}>
                  {partner.name}
                </option>
              ))}
            </select>
            {errors.partner_id && (
              <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.partner_id}</p>
            )}
            {isPartner && (
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Users will be created in your organization
              </p>
            )}
          </div>
        )}

        {/* Modules Selection */}
        {availableModules.length > 0 && (
          <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
            <button
              type="button"
              onClick={() => setShowModulesSection(!showModulesSection)}
              className="w-full flex items-center justify-between text-left"
            >
              <div>
                <h3 className="text-sm font-medium text-gray-900 dark:text-white">
                  Modules to Inherit
                </h3>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  {formData.modules?.length || 0} of {availableModules.length} selected
                </p>
              </div>
              {showModulesSection ? (
                <ChevronUp className="w-5 h-5 text-gray-500" />
              ) : (
                <ChevronDown className="w-5 h-5 text-gray-500" />
              )}
            </button>
            
            {showModulesSection && (
              <div className="mt-4 space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-xs text-gray-600 dark:text-gray-400">
                    Select modules to grant access
                  </span>
                  <div className="space-x-2">
                    <button
                      type="button"
                      onClick={handleSelectAllModules}
                      className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
                    >
                      Select All
                    </button>
                    <button
                      type="button"
                      onClick={handleDeselectAllModules}
                      className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
                    >
                      Deselect All
                    </button>
                  </div>
                </div>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {availableModules.map((module) => (
                    <label
                      key={module}
                      className="flex items-start space-x-2 p-2 hover:bg-gray-50 dark:hover:bg-gray-800 rounded cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={formData.modules?.includes(module) || false}
                        onChange={() => handleModuleToggle(module)}
                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 mt-0.5"
                      />
                      <div className="flex-1 min-w-0">
                        <span className="text-sm text-gray-900 dark:text-white">
                          {(moduleLabels as Record<string, string>)[module] || module}
                        </span>
                        {(moduleDescriptions as Record<string, string>)[module] && (
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                            {(moduleDescriptions as Record<string, string>)[module]}
                          </p>
                        )}
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Permissions Selection */}
        {(permissionsLoading || availablePermissions.length > 0) && (
          <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
            <button
              type="button"
              onClick={() => setShowPermissionsSection(!showPermissionsSection)}
              className="w-full flex items-center justify-between text-left"
            >
              <div>
                <h3 className="text-sm font-medium text-gray-900 dark:text-white">
                  Permissions to Inherit
                </h3>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  {formData.permissions?.length || 0} of {availablePermissions.length} selected
                </p>
              </div>
              {showPermissionsSection ? (
                <ChevronUp className="w-5 h-5 text-gray-500" />
              ) : (
                <ChevronDown className="w-5 h-5 text-gray-500" />
              )}
            </button>
            
            {showPermissionsSection && (
              <div className="mt-4 space-y-3">
                {permissionsLoading ? (
                  <div className="flex items-center justify-center py-4">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
                  </div>
                ) : (
                  <>
                    <div className="flex justify-between items-center">
                      <span className="text-xs text-gray-600 dark:text-gray-400">
                        Select permissions to grant
                      </span>
                      <div className="space-x-2">
                        <button
                          type="button"
                          onClick={handleSelectAllPermissions}
                          className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
                        >
                          Select All
                        </button>
                        <button
                          type="button"
                          onClick={handleDeselectAllPermissions}
                          className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
                        >
                          Deselect All
                        </button>
                      </div>
                    </div>
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                      {permissionModuleOrder.map((moduleKey) => {
                        const modulePermissions = groupedPermissions[moduleKey] || [];
                        if (modulePermissions.length === 0) {
                          return null;
                        }

                        const selectedCount = modulePermissions.filter(permission =>
                          formData.permissions?.includes(permission.name)
                        ).length;

                        const allModuleSelected = modulePermissions.every(p => formData.permissions?.includes(p.name));
                        const someModuleSelected = !allModuleSelected && modulePermissions.some(p => formData.permissions?.includes(p.name));

                        return (
                          <div key={moduleKey} className="border border-gray-200 dark:border-gray-700 rounded-md">
                            <div className="flex items-center px-3 py-2 hover:bg-gray-50 dark:hover:bg-gray-800 rounded-md">
                              <input
                                type="checkbox"
                                checked={allModuleSelected}
                                ref={el => {
                                  if (el) el.indeterminate = someModuleSelected;
                                }}
                                onChange={() => handleModulePermissionsToggle(moduleKey)}
                                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 mr-3 flex-shrink-0"
                              />
                              <button
                                type="button"
                                onClick={() => togglePermissionModule(moduleKey)}
                                className="flex-1 flex items-center justify-between text-left"
                              >
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center">
                                    <span className="text-sm font-medium text-gray-900 dark:text-white">
                                      {moduleLabels[moduleKey] || moduleKey}
                                    </span>
                                    <span className="text-xs text-gray-500 dark:text-gray-400 ml-2">
                                      {selectedCount} of {modulePermissions.length}
                                    </span>
                                  </div>
                                  {moduleDescriptions[moduleKey] && (
                                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                      {moduleDescriptions[moduleKey]}
                                    </p>
                                  )}
                                </div>
                                {expandedPermissionModules[moduleKey] ? (
                                  <ChevronUp className="w-4 h-4 text-gray-500" />
                                ) : (
                                  <ChevronDown className="w-4 h-4 text-gray-500" />
                                )}
                              </button>
                            </div>

                            {expandedPermissionModules[moduleKey] && (
                              <div className="px-2 pb-2 space-y-1">
                                {modulePermissions.map((permission) => (
                                  <label aria-label={permission.description || permission.name}
                                    key={permission.name}
                                    className="flex items-start space-x-2 p-2 hover:bg-gray-50 dark:hover:bg-gray-800 rounded cursor-pointer"
                                  >
                                    <input
                                      type="checkbox"
                                      checked={formData.permissions?.includes(permission.name) || false}
                                      onChange={() => handlePermissionToggle(permission.name)}
                                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 mt-0.5"
                                    />
                                    <div className="flex-1 min-w-0">
                                      <span className="text-sm text-gray-900 dark:text-white">
                                        {permission.description || permission.name}
                                      </span>
                                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                        {permission.name}
                                      </p>
                                    </div>
                                  </label>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        )}

        {/* Submit Error */}
        {errors.submit && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-start">
            <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 mr-3 mt-0.5 flex-shrink-0" />
            <p className="text-sm text-red-800 dark:text-red-300">{errors.submit}</p>
          </div>
        )}

        {/* Actions */}
        <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-700">
          <Button
            type="button"
            variant="secondary"
            onClick={handleClose}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            loading={loading}
            disabled={loading}
          >
            Add User
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default AddUserModal;
